import { __awaiter } from "tslib";
import React, { useMemo } from 'react';
import 'brace';
import 'brace/mode/html';
import 'brace/theme/tomorrow';
import './polyfills';
import './styles.css';
import { createBrowserRouter, Navigate, RouterProvider, useRouteError, } from 'react-router';
import { Enrich } from '@/pages/enrich/enrich';
import { AppLayout, LoggedInLayout } from '@/app/layouts';
import { SearchForRetailLayout } from '@/search-for-retail/search-for-retail.layout';
import { SearchForRetailRoutes as S4RRoutes } from '@/search-for-retail/search-for-retail.routes';
import { SearchForRetailUtilities as S4RUtils } from '@/search-for-retail/utilities';
import { LoggedInGuard, LoggedOutGuard } from '@/routing';
import { FeatureFlag, useFeatureFlagsProvider, } from '@/search-for-retail/feature-flags';
import { ReleaseNotes } from '@/pages/release-notes';
import FullScreenErrorOverlay from '@/error-boundary/error-screen.component.';
import { AuthProvider } from '@/auth';
import { GbLoading } from '@/components/gb-loading';
import { GlobalStyle } from './app.styles';
import { Routing, SectionPath } from '@/constants';
const ChunkLoadErrorFallback = () => {
    const error = useRouteError();
    const isChunkLoadError = error instanceof Error && error.name === 'ChunkLoadError';
    if (!isChunkLoadError) {
        return null;
    }
    return (<FullScreenErrorOverlay />);
};
const getGuardedLogin = () => __awaiter(void 0, void 0, void 0, function* () {
    const { default: Login } = yield import('@/pages/login' /* webpackChunkName: "s4r-login" */);
    const Wrapper = () => <LoggedOutGuard authNavigateTo={S4RUtils.navigateTo()}>
    <Login />
  </LoggedOutGuard>;
    return { Component: Wrapper };
});
const GuardedLoggedInLayout = () => (<LoggedInGuard>
    <LoggedInLayout />
  </LoggedInGuard>);
const createRouter = (featureFlagsService) => createBrowserRouter([
    {
        id: 'root',
        element: <AppLayout />,
        errorElement: <ChunkLoadErrorFallback />,
        children: [
            {
                path: '/',
                element: <GuardedLoggedInLayout />,
                errorElement: <ChunkLoadErrorFallback />,
                children: [
                    {
                        index: true,
                        element: <Navigate to={S4RUtils.navigateTo()}/>,
                        errorElement: <ChunkLoadErrorFallback />,
                    },
                    {
                        path: SectionPath.MERCHANDISING,
                        element: <SearchForRetailLayout />,
                        children: S4RRoutes.getRoutes(featureFlagsService),
                        errorElement: <ChunkLoadErrorFallback />,
                    },
                    {
                        path: `${SectionPath.ENRICH}/*`,
                        element: <Enrich />,
                        errorElement: <ChunkLoadErrorFallback />,
                    },
                    {
                        path: `${SectionPath.RELEASE_NOTES}/:search?`,
                        element: <ReleaseNotes />,
                        errorElement: <ChunkLoadErrorFallback />,
                    },
                    {
                        path: '*',
                        element: <Navigate to={S4RUtils.navigateTo()}/>,
                        errorElement: <ChunkLoadErrorFallback />,
                    },
                ],
            },
            {
                path: Routing.LOGIN_PATH,
                lazy: getGuardedLogin,
                errorElement: <ChunkLoadErrorFallback />,
            },
        ],
    },
]);
export const App = () => {
    const { featureFlagsService } = useFeatureFlagsProvider();
    const enableFontSize = featureFlagsService.getFeatureFlagConfig(FeatureFlag.enableFontSize);
    if (enableFontSize === null || enableFontSize === void 0 ? void 0 : enableFontSize.enabled) {
        document.querySelector('html').classList.add('smaller-font-size');
    }
    const router = useMemo(() => createRouter(featureFlagsService), [featureFlagsService]);
    return (<AuthProvider>
      {(enableFontSize === null || enableFontSize === void 0 ? void 0 : enableFontSize.enabled) && <GlobalStyle fontSize={enableFontSize.fontSize}/>}
      <GbLoading>
        <RouterProvider router={router}/>
      </GbLoading>
    </AuthProvider>);
};
